import { useEffect, useState, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import GoogleAuthCallback from "./components/GoogleAuthCallback";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import PopupMessage from "@/components/PopupMessage";
import RouteErrorBoundary from "@/components/RouteErrorBoundary";
// GlobalBannerAd import removed Feb 22 2026 — mount site removed below.
import { toast } from "sonner";
import { NotificationProvider, useNotification } from "@/context/NotificationContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { PageTitleUpdater } from "@/hooks/usePageTitle";
// ToastContainer removed - using Sonner toast only
import OfflineIndicator from "@/components/OfflineIndicator";
import UpdateBanner from "@/components/UpdateBanner";
import SmartAppBanner from "@/components/SmartAppBanner";
import MobileAppGate from "@/components/MobileAppGate";
import TopBar from "@/components/TopBar";
import BottomNav from "@/components/BottomNav";
import LiveTickerStrip from "@/components/LiveTickerStrip";
import WebVitalsReporter from "@/components/WebVitalsReporter";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import ServiceChargePendingBanner from "@/components/ServiceChargePendingBanner";
import AdminOnWebOnly from "@/components/AdminOnWebOnly";
import { applyBrandedStatusBar } from "@/utils/nativeUx";
import { useAdMob } from "@/hooks/useAdMob";
import { captureInstallReferrer } from "@/utils/installReferrer";
import { cancelSessionExpiryWarning } from "@/utils/sessionNotifications";
import { syncAppBadgeFromBackend, clearAppBadge } from "@/utils/appBadge";

// ============================================================
// GLOBAL TOAST FILTER (May 10, 2026)
// ------------------------------------------------------------
// Suppress transient toast noise that anxiously interrupts admins:
//   - "Database is busy right now…" (Atlas peak-load timeout)
//   - Generic "Failed to fetch …" when the underlying cause was a DB-busy
//     error (errored axios call's response.data.detail bubbled into a
//     hardcoded toast string in a component).
// These errors are 100% transient and the auto-refresh tick that follows
// recovers fresh data — so a toast adds zero user value and a lot of
// frustration. We log them to the console for debugging instead.
// ============================================================
const TOAST_SUPPRESS_PATTERNS = [
  /database is busy right now/i,
  /database is busy/i,
];
const _origToastError = toast.error.bind(toast);
toast.error = (message, opts) => {
  try {
    const text = typeof message === "string"
      ? message
      : (message && message.toString ? message.toString() : "");
    if (TOAST_SUPPRESS_PATTERNS.some((rx) => rx.test(text))) {
      // eslint-disable-next-line no-console
      console.warn("[toast.error suppressed]", text);
      return null;
    }
  } catch (_) { /* fall through and show original */ }
  return _origToastError(message, opts);
};
// ============================================================
// Lazy load heavy components
const SupportChatbot = null; // REMOVED - chatbot feature removed
// HoliCelebration removed as per user request
// import HoliCelebration from "@/components/HoliCelebration";

// Check if this is a user-only build (for Play Store AAB)
const IS_USER_BUILD = process.env.REACT_APP_BUILD_TYPE === 'user';

// Only import AdminLayout if not user build
const AdminLayout = IS_USER_BUILD ? null : lazy(() => import("@/components/layouts/AdminLayout"));

// ManagerLayout removed - Manager uses AdminLayout with permission-based access
// StockistLayout removed - stockist system deprecated

// ============================================================
// ADMIN IMPERSONATION: per-tab storage + URL hash handoff
// New impersonation tabs open with URL hash `#imp=<base64(json)>`.
// We parse it here (before any React render or localStorage read),
// store payload in sessionStorage (tab-scoped), then clear the hash.
// This avoids any race with admin's shared localStorage.
// ============================================================
(function handleImpersonationHash() {
  try {
    const hash = typeof window !== 'undefined' ? window.location.hash : '';
    if (!hash || hash.indexOf('imp=') === -1) return;
    const m = hash.match(/imp=([^&]+)/);
    if (!m) return;
    const payload = JSON.parse(atob(decodeURIComponent(m[1])));
    if (!payload || !payload.uid || !payload.session_token) return;
    sessionStorage.setItem('paras_imp_active', '1');
    sessionStorage.setItem('paras_user', JSON.stringify(payload));
    // Remove hash from URL without reloading
    if (window.history && window.history.replaceState) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  } catch (e) {
    console.warn('[IMP] hash parse failed', e);
  }
})();

// Tab-scoped storage helpers: prefer sessionStorage when impersonating
const isImpersonationTab = () => {
  try { return sessionStorage.getItem('paras_imp_active') === '1'; } catch { return false; }
};
const getStoredUserRaw = () => {
  try {
    if (isImpersonationTab()) return sessionStorage.getItem('paras_user');
    return localStorage.getItem('paras_user');
  } catch { return null; }
};
const setStoredUserRaw = (value) => {
  try {
    if (isImpersonationTab()) sessionStorage.setItem('paras_user', value);
    else localStorage.setItem('paras_user', value);
  } catch {}
};
const removeStoredUser = () => {
  try {
    if (isImpersonationTab()) {
      sessionStorage.removeItem('paras_user');
      sessionStorage.removeItem('paras_imp_active');
    } else {
      localStorage.removeItem('paras_user');
    }
  } catch {}
};
// Expose for non-React callers if needed
if (typeof window !== 'undefined') {
  window.__parasStorage = { isImpersonationTab, getStoredUserRaw, setStoredUserRaw, removeStoredUser };
}

// Axios defaults for mobile - longer timeout
axios.defaults.timeout = 30000; // 30 seconds for slow mobile networks

// ============================================================================
// AUTO-RETRY for transient failures (504 Gateway Timeout, 502 Bad Gateway,
// 503 Service Unavailable, ECONNABORTED). Eliminates the "refresh 3-4 times
// to load admin pages" pattern. Single retry with 800ms back-off; safe only
// for idempotent methods (GET, HEAD).
//
// May 2026: Extended to whitelisted POST endpoints that the backend has
// made idempotent (approve / reject / pay-with-prc / bank-transfer all
// short-circuit on duplicate state). This eliminates the "Server is busy"
// toast that admins were hitting during Atlas peak load.
// ----------------------------------------------------------------------------
const RETRY_STATUS_CODES = [502, 503, 504];
const RETRY_METHODS = ['get', 'head'];
// Idempotent POST endpoints — re-running them is safe at the backend.
const RETRY_POST_PATHS = [
  '/admin/vip-payment/',          // approve / reject (status CAS guards)
  '/subscription/pay-with-prc',   // idempotency_key + cooldown CAS
  '/subscription/payment/',       // manual UTR submission (idempotency_key)
  '/bank-transfer/request',       // idempotency_key + balance CAS
  '/bill-payment/request',        // idempotency_key + balance CAS
  '/bank-redeem/request/',        // idempotency_key + balance CAS
];
const MAX_RETRIES = 1;
const RETRY_DELAY_MS = 800;
// May 10, 2026 — Production Atlas was returning 500s with the friendly
// "Database is busy" message on transient mongo timeouts. We treat them
// the same as 503/504 for retry purposes AND tag the resulting error so
// callers (e.g., AdminKYC auto-refresh) can silently swallow them.
const FRIENDLY_DB_BUSY_TEXT = 'Database is busy right now';
// ============================================================================

// ============================================================================
// GLOBAL FETCH WRAPPER (Jul 2026)
// ----------------------------------------------------------------------------
// A large chunk of the app still calls window.fetch() directly (PRCStatement,
// AdminPopupMessages, AdminLedgerView, NotificationContext, KYCVerification,
// ProfileAdvanced, App.js's auth-me/logout/validate-session calls, etc.).
//
// The axios interceptor above only catches responses that flow through axios —
// so a raw fetch() that gets 401 back would previously leave the user
// wedged in the logged-in shell with silently-failing pages (exactly the
// bug reported on Jul 3, 2026). To close that gap without touching every
// call-site, we monkey-patch window.fetch once here to piggy-back the same
// 401 detection + one-shot logout redirect logic.
//
// Safe because: we always call through to the real underlying fetch; we
// never mutate the request; we only ADD post-response side-effects, and
// only fire the logout redirect once via a window-scoped guard flag.
// ============================================================================
if (typeof window !== 'undefined' && !window.__parasFetchPatched) {
  const _originalFetch = window.fetch.bind(window);
  window.fetch = async function patchedFetch(input, init) {
    // Normalise call signature so we can safely mutate headers.
    let request = init || {};
    const urlStr = typeof input === 'string' ? input : (input?.url || '');
    const isApiCall = urlStr.includes('/api/');
    const isAuthEndpoint = urlStr.includes('/api/auth/login')
      || urlStr.includes('/api/auth/register')
      || urlStr.includes('/api/auth/verify-otp')
      || urlStr.includes('/api/auth/forgot-pin')
      || urlStr.includes('/api/auth/logout')
      || urlStr.includes('/api/auth/validate-session');

    // AUTO-INJECT Bearer token for /api/ calls that don't already carry one.
    // Many legacy fetch() call sites forgot to add the Authorization header;
    // rather than patch every call site we transparently attach the token
    // stored by the axios request interceptor. This makes raw fetch behave
    // the same as axios for auth purposes.
    let tokenWasAttached = false;
    if (isApiCall && !isAuthEndpoint) {
      const existingHeaders = new Headers(request.headers || (typeof input !== 'string' && input?.headers) || {});
      if (!existingHeaders.has('Authorization')) {
        const stored = localStorage.getItem('token') || localStorage.getItem('paras_session_token');
        if (stored) {
          existingHeaders.set('Authorization', `Bearer ${stored}`);
          request = { ...request, headers: existingHeaders };
          tokenWasAttached = true;
        }
      } else {
        tokenWasAttached = true;
      }
    }

    const response = await _originalFetch(input, request);
    try {
      const status = response.status;
      // Only trigger the session-expired flow when we ACTUALLY sent a token
      // and the server rejected it. A 401 with no token attached just means
      // the endpoint required auth and the caller hadn't logged in yet —
      // not a session-expiry event.
      if (status === 401 && !isAuthEndpoint && tokenWasAttached && !window.__parasAuthLogoutInProgress) {
        window.__parasAuthLogoutInProgress = true;
        localStorage.removeItem('paras_user');
        sessionStorage.removeItem('paras_user');
        localStorage.removeItem('paras_session_token');
        localStorage.removeItem('token');
        try {
          toast.error('Your session has expired. Please log in again.', { duration: 5000 });
        } catch (_) { /* toast lib not initialised */ }
        setTimeout(() => {
          const path = window.location.pathname || '';
          if (!path.startsWith('/login') && !path.startsWith('/register') && path !== '/') {
            window.location.href = '/login';
          } else {
            window.__parasAuthLogoutInProgress = false;
          }
        }, 400);
      }
    } catch (_) { /* never crash the request pipeline */ }
    return response;
  };
  window.__parasFetchPatched = true;
}

// Axios response interceptor - suppress 404 toast errors for optional APIs
axios.interceptors.response.use(
  response => response,
  async error => {
    const config = error.config || {};
    const url = config.url || '';
    const method = (config.method || 'get').toLowerCase();
    const status = error.response?.status;
    const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout');
    const isIdempotentPostPath = method === 'post' && RETRY_POST_PATHS.some(p => url.includes(p));
    const detailText = (error.response?.data?.detail || '').toString();
    const isFriendlyDBBusy = status === 500 && detailText.includes(FRIENDLY_DB_BUSY_TEXT);
    const isRetriable =
      (RETRY_METHODS.includes(method) || isIdempotentPostPath) &&
      (RETRY_STATUS_CODES.includes(status) || isTimeout || isFriendlyDBBusy);

    // Auto-retry once for transient gateway failures / timeouts on GET requests
    if (isRetriable) {
      config.__retryCount = (config.__retryCount || 0);
      if (config.__retryCount < MAX_RETRIES) {
        config.__retryCount += 1;
        await new Promise(r => setTimeout(r, RETRY_DELAY_MS));
        try {
          return await axios(config);
        } catch (retryErr) {
          // fall through to normal error handling
          error = retryErr;
        }
      }
    }

    // Tag transient "Database is busy" errors so callers can silently swallow.
    const finalDetail = (error.response?.data?.detail || '').toString();
    if (error.response?.status === 500 && finalDetail.includes(FRIENDLY_DB_BUSY_TEXT)) {
      error.__isFriendlyDBBusy = true;
    }

    // ────────────────────────────────────────────────────────────────────
    // AUTH GUARD (Jul 2026): When any protected endpoint responds with 401
    // (or a "token expired / invalid" flavored 403), the JWT has aged out on
    // the client. Previously we just rejected the promise, which meant the
    // logged-in shell kept rendering with stale data while every fetch
    // silently failed — users could navigate around with no clue they were
    // "logged out". We now force a single, atomic global logout:
    //   • wipes stored user + tokens
    //   • toasts a friendly message
    //   • redirects to /login
    // Skips the login endpoint itself (401 there = bad credentials, not
    // expiry) and the already-redirecting login page.
    // Uses a window-scoped flag so simultaneous stale requests can't fire
    // multiple redirects / toasts.
    // ────────────────────────────────────────────────────────────────────
    const finalStatus = error.response?.status;
    const isAuthEndpoint = url.includes('/api/auth/login') || url.includes('/api/auth/register') || url.includes('/api/auth/verify-otp');
    // Feb 2026 — regex was too broad: "Invalid admin operation PIN" (from
    // admin PIN-gated endpoints like /admin/partners/assign) matched
    // /invalid/i and force-logged the admin out. Now we require an actual
    // auth/token/session context keyword before treating a 403 as a
    // session-expiry event. Non-session 403s (wrong PIN, forbidden role,
    // IP block) surface as normal toast errors to the calling component.
    const tokenExpiredKeywords = /token[ _-]?(expired|invalid)|(expired|invalid)[ _-]?token|jwt|session[ _-]?(expired|invalid|timeout)|authentication (required|failed|expired)|unauthori[sz]ed|please log ?in again/i;
    const isTokenIssue = (finalStatus === 401) ||
      (finalStatus === 403 && tokenExpiredKeywords.test(finalDetail));

    if (isTokenIssue && !isAuthEndpoint && !window.__parasAuthLogoutInProgress) {
      // Only trigger for authenticated users — check that we actually SENT
      // a token on this request. A 401 with no Authorization header just
      // means the endpoint required auth (public flow), not that a live
      // session expired. Prevents false-positive logout kicks on legacy
      // call-sites that forgot to attach the token.
      const sentAuthHeader = !!(config.headers && (config.headers.Authorization || config.headers.authorization));
      if (sentAuthHeader) {
        window.__parasAuthLogoutInProgress = true;
        try {
          localStorage.removeItem('paras_user');
          sessionStorage.removeItem('paras_user');
          localStorage.removeItem('paras_session_token');
          localStorage.removeItem('token');
          // Best-effort user feedback — don't crash if toast isn't ready yet
          try {
            toast.error('Your session has expired. Please log in again.', { duration: 5000 });
          } catch (_) { /* toast lib not initialised */ }
        } finally {
          // Redirect after a short pause so the toast has time to appear.
          // Full page reload guarantees every in-memory user state, timer
          // and interval is reset — no dangling authenticated components.
          setTimeout(() => {
            const path = window.location.pathname || '';
            if (!path.startsWith('/login') && !path.startsWith('/register') && path !== '/') {
              window.location.href = '/login';
            } else {
              window.__parasAuthLogoutInProgress = false;
            }
          }, 400);
        }
      }
    }

    // Don't show toast for 404 errors on optional/stats endpoints
    const is404 = error.response?.status === 404;
    const isOptionalEndpoint = url.includes('subscription-stats') || 
                               url.includes('pricing-reference') ||
                               url.includes('dashboard-stats');
    
    if (is404 && isOptionalEndpoint) {
      console.warn(`Optional API not found: ${url}`);
      return Promise.reject(error); // Reject but don't show toast
    }
    
    // Handle timeout errors gracefully
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      console.warn('Request timeout:', url);
    }
    
    return Promise.reject(error);
  }
);

// Axios request interceptor - add cache busting for admin APIs to prevent stale data
axios.interceptors.request.use(
  config => {
    const url = config.url || '';
    
    // Add auth token to all API requests
    if (!config.headers?.['Authorization']) {
      let token = null;
      const storedUser = getStoredUserRaw();
      if (storedUser) {
        try {
          const user = JSON.parse(storedUser);
          if (user.token) token = user.token;
        } catch (e) {
          // ignore parse errors
        }
      }
      // Fallback: check direct token keys
      if (!token) token = localStorage.getItem("token") || localStorage.getItem("paras_session_token");
      
      if (token) {
        config.headers = config.headers || {};
        config.headers['Authorization'] = `Bearer ${token}`;
      }
    }
    
    // Add cache busting for admin GETs ONLY when caller asks via `?refresh=1`.
    // Default: let server-side redis cache + browser cache serve fresh data.
    // (Apr 30 2026 perf — was bypassing cache on every admin GET, hurting hit-rate.)
    if (url.includes('/admin/') && config.method === 'get' && config.params && config.params.refresh) {
      config.params._t = Date.now();
      config.headers = config.headers || {};
      config.headers['Cache-Control'] = 'no-cache';
      config.headers['Pragma'] = 'no-cache';
    }
    // Increase timeout for mobile users on slow connections.
    // Admin endpoints often touch heavy aggregations → bump to 60s to
    // ride out cold-cache requests rather than throwing the user a 504.
    if (!config.timeout) {
      config.timeout = url.includes('/admin/') ? 60000 : 30000;
    }
    return config;
  },
  error => Promise.reject(error)
);

// Helper function to check if user can access admin pages
const canAccessAdmin = (user) => {
  return user && (user.role === "admin" || user.role === "sub_admin" || user.role === "manager");
};

// Helper function to check if user is admin/manager (should not access user pages)
const isAdminOrManager = (user) => {
  return user && (user.role === "admin" || user.role === "sub_admin" || user.role === "manager");
};

// Loading component - optimized with skeleton
const LoadingFallback = () => {
  // ── Suspense watchdog (Jun 2026) ────────────────────────────────────
  // When a lazy chunk fetch returns HTML (because Emergent's static
  // host falls back to index.html for unknown /static/* paths), the
  // Suspense boundary stays in "Loading" forever. If we're still on
  // this fallback after 12 seconds we treat it as a poisoned cache
  // and trigger a cache-busted reload so the browser pulls fresh HTML.
  useEffect(() => {
    const SUSPENSE_HEAL_KEY = 'paras_suspense_heal_ts';
    const t = setTimeout(() => {
      try {
        const last = parseInt(sessionStorage.getItem(SUSPENSE_HEAL_KEY) || '0', 10);
        if (Date.now() - last < 20000) return; // already healed recently
        sessionStorage.setItem(SUSPENSE_HEAL_KEY, String(Date.now()));
      } catch (_) { /* private mode: continue */ }

      (async () => {
        try {
          if ('caches' in window) {
            const names = await caches.keys();
            await Promise.all(names.map((n) => caches.delete(n)));
          }
          if ('serviceWorker' in navigator) {
            const regs = await navigator.serviceWorker.getRegistrations();
            await Promise.all(regs.map((r) => r.unregister()));
          }
        } catch (_) { /* best effort */ }

        try {
          const u = new URL(window.location.href);
          u.searchParams.set('_sb', Date.now().toString(36));
          window.location.replace(u.toString());
        } catch (_) {
          window.location.reload();
        }
      })();
    }, 12000);
    return () => clearTimeout(t);
  }, []);

  return (
  <div className="min-h-screen bg-gray-950 flex items-center justify-center">
    <div className="flex flex-col items-center gap-5">
      <div className="relative" style={{ width: 100, height: 100 }}>
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="absolute" style={{ width: 6, height: 6, top: `${[5,-2,50,95,102,50][i]}%`, left: `${[-5,50,105,-5,50,105][i]}%`, animation: `starBlink 1.5s ease-in-out ${i*0.25}s infinite` }}>
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full text-amber-400"><path d="M12 2l2.09 6.26L20.18 9l-5.09 3.74L16.18 19 12 15.27 7.82 19l1.09-6.26L3.82 9l6.09-.74L12 2z" /></svg>
          </div>
        ))}
        <img src="/paras-logo.png" alt="Loading" className="w-full h-full object-contain" style={{ animation: 'logoPulse 2s ease-in-out infinite' }} />
      </div>
      <p className="text-gray-400 text-sm font-medium" style={{ animation: 'textFade 2s ease-in-out infinite' }}>Loading...</p>
      <style>{`
        @keyframes logoPulse { 0%,100%{transform:scale(1)} 50%{transform:scale(1.08)} }
        @keyframes starBlink { 0%,100%{opacity:0;transform:scale(0.5)} 50%{opacity:1;transform:scale(1.2)} }
        @keyframes textFade { 0%,100%{opacity:0.4} 50%{opacity:1} }
      `}</style>
    </div>
  </div>
  );
};

// Lazy load all pages for better performance
// Home.js and HomeFintech.js removed - using RewardsHome as main landing page
const HowItWorks = lazy(() => import("@/pages/HowItWorks"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogArticle = lazy(() => import("@/pages/BlogArticle"));
const Login = lazy(() => import("@/pages/LoginNew"));
const RegisterSimple = lazy(() => import("@/pages/RegisterSimple"));
const SetNewPin = lazy(() => import("@/pages/SetNewPin"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPasswordNew"));
const ResetPassword = lazy(() => import(/* webpackChunkName: "auth" */ "@/pages/ResetPassword"));
const ForgotPin = lazy(() => import("@/pages/ForgotPin"));
const SupportTickets = lazy(() => import("@/pages/SupportTickets"));
// Dashboard.js, DashboardNew.js, DashboardPremium.js removed - not in use

// CRITICAL PAGES - Preload after initial render for faster navigation
const DashboardModern = lazy(() => import("@/pages/DashboardModern"));
// Mining page removed - integrated into Dashboard (April 2026)

// Preload critical pages after initial render
const preloadCriticalPages = () => {
  setTimeout(() => {
    import("@/pages/DashboardModern");
  }, 2000);
};

// Call preload when module loads
if (typeof window !== 'undefined') {
  preloadCriticalPages();
}

// TapGame removed - feature deprecated
const Referrals = lazy(() => import("@/pages/ReferralsEnhanced"));
const MyReferralBonus = lazy(() => import("@/pages/MyReferralBonus"));
const MyServiceCharges = lazy(() => import("@/pages/MyServiceCharges"));
const DownlineLiveFeed = lazy(() => import("@/pages/DownlineLiveFeed"));
// Marketplace & Orders removed - feature deprecated (December 2025)
// const Orders = lazy(() => import("@/pages/Orders"));
// VIPMembership removed - replaced by SubscriptionPlans (new 4-tier system)
const SubscriptionPlans = lazy(() => import("@/pages/SubscriptionPlans"));
const BankRedeemPage = lazy(() => import("@/pages/BankRedeemPage"));
// Removed: WalletNew (withdrawal functionality removed)
// Removed: Leaderboard (feature removed for AdMob compliance)
// Removed: ReferralEarningsHistory (feature removed)
const ProfileAdvanced = lazy(() => import("@/pages/ProfileAdvanced"));
// Removed: ReferralDashboard, ReferralDashboardAI - using ReferralsEnhanced
// FlashSalesPage - REMOVED (deprecated April 2026)
// Removed: MyActivity - feature removed

// Social Feature Pages
const PublicProfile = lazy(() => import("@/pages/PublicProfile"));
const NetworkFeed = lazy(() => import("@/pages/NetworkFeed"));
const Messages = lazy(() => import("@/pages/Messages"));
const FollowersList = lazy(() => import("@/pages/FollowersList"));
const Notifications = lazy(() => import("@/pages/Notifications"));
// ParasLuxuryLife - REMOVED (deprecated feature)
// ParasRecurringDeposit - REMOVED (deprecated feature)
// NetworkTreeAdvanced - REMOVED (orphaned page, no nav link, used deprecated /referrals/network-tree endpoint)
const MyInvoices = lazy(() => import("@/pages/MyInvoices"));
const ParasMall = lazy(() => import("@/pages/ParasMall"));
const MallWishlist = lazy(() => import("@/pages/MallWishlist"));
const AdminParasMall = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/Admin/AdminParasMall"));
const AdminMallAnalytics = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/Admin/AdminMallAnalytics"));
const MyReports = lazy(() => import("@/pages/MyReports"));

// ============ ADMIN PAGES - Code Split into separate chunk ============
// These pages are only loaded when admin users access them (~1% of users)
// This reduces initial bundle size for regular users by ~30%
// ADMIN PAGES - Only loaded if NOT user build (excluded from Play Store AAB)
const AdminDashboard = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminDashboard"));
const AdminObservability = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminObservability"));
// AdminDashboardModern removed - not in use
const AdminAnalytics = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminAnalytics"));
const AdminPRCAnalytics = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminPRCAnalytics"));
const AdminAuditService = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminAuditService"));
const AdminProfitLoss = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminProfitLoss"));
const AdminGSTReport = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminGSTReport"));
const AdminInvoicesList = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminInvoicesList"));
const AdminBulkRefundOTP = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminBulkRefundOTP"));
const AdminLiquidity = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminLiquidity"));
const AdminCompanyWallets = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminCompanyWallets"));
const AdminCashBankBook = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminCashBankBook"));
const AdminPRCLedger = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminPRCLedger"));
const AdminTransactionManager = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminTransactionManager"));
const AdminFinancialReports = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminFinancialReports"));
const AdminCapitalManagement = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminCapitalManagement"));
const AdminTrialBalance = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminTrialBalance"));
const AdminAccountsReceivable = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminAccountsReceivable"));
const AdminAccountsPayable = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminAccountsPayable"));
const AdminFinancialRatios = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminFinancialRatios"));
const AdminAdsIncome = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminAdsIncome"));
const AdminFraudAlerts = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminFraudAlerts"));
const AdminFraudDashboard = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminFraudDashboard"));
const AdminVideoAds = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminVideoAds"));
const AdminFixedExpenses = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminFixedExpenses"));
const AdminKYC = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminKYC"));
const AdminInactiveCleanup = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminInactiveCleanup"));
const AdminPRCLock = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminPRCLock"));
const AdminEconomySettings = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminEconomySettings"));
// AdminDMTRefunds and AdminDMTTransactions REMOVED - Eko API not working
// AdminOrders removed - Marketplace deprecated (December 2025)
// const AdminOrders = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminOrders"));
const AdminSupport = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminSupport"));
// AdminChatbotWithdrawals REMOVED - feature deprecated (March 2026)
const AdminContactSubmissions = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminContactSubmissions"));
const AdminContactSettings = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminContactSettings"));
// AdminMarketplace removed - feature deprecated
const AdminSettings = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminSettings"));
// AdminPaymentSettings, AdminSystemSettings, AdminWebSettings, AdminSocialMediaSettings, AdminRedeemSettings removed - merged into AdminSettingsHub
const AdminSettingsHub = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminSettingsHub"));
const AdminPartners = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminPartners"));
const AdminPartnerStores = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminPartnerStores"));
const PartnerStoreDashboard = lazy(() => import(/* webpackChunkName: "partner-store" */ "@/pages/PartnerStoreDashboard"));
const PayPartnerStore = lazy(() => import(/* webpackChunkName: "partner-store" */ "@/pages/PayPartnerStore"));
const AdminDeviceBinding = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminDeviceBinding"));
const ChangeDevice = lazy(() => import(/* webpackChunkName: "auth" */ "@/pages/ChangeDevice"));
// AdminPRCRateControl - REMOVED (June 2026, fixed 10 PRC = ₹1)
const AdminSecurityDashboard = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminSecurityDashboard"));
// AdminVIPPlans and AdminVIPPaymentVerification removed - replaced by AdminSubscriptionManagement (new 4-tier system)
const AdminSubscriptionManagement = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminSubscriptionManagement"));
// AdminMembers — REMOVED (May 5, 2026) Members Dashboard page deleted per product request.
// AdminPRCBurnControl - REMOVED (burning concept deprecated)
const AdminDataBackup = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminDataBackup"));
// AdminBillPayments - REMOVED (all pending rejected + refunded)
const AdminRazorpaySubscriptions = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminRazorpaySubscriptions"));
const AdminErrorMonitor = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/Admin/ErrorMonitor"));
const AdminHealthCheck = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/Admin/AdminHealthCheck"));
const AdminForceActivateSubscription = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/Admin/AdminForceActivateSubscription"));
const AdminWebVitals = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/Admin/AdminWebVitals"));
// AdminBankWithdrawals removed - merged into AdminUnifiedPayments
// AdminGiftVouchers - REMOVED (feature discontinued May 2026)
const AdminServiceCharges = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/Admin/AdminServiceCharges"));
const AdminFinanceMetrics = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/Admin/AdminFinanceMetrics"));
const AdminBillServiceCharges = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminBillServiceCharges"));
const AdminServiceToggles = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminServiceToggles"));
const AdminPolicies = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminPolicies"));
const AdminUserLedger = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminUserLedger"));
// AdminPRCRain removed - feature deleted
// AdminRedeemSettings, AdminRedeemDashboard - REMOVED (no pending requests, feature deprecated March 2026)
const AdminPendingRequests = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminPendingRequests"));
const AdminAccountingDashboard = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminAccountingDashboard"));
// AdminPRCEconomyDashboard - REMOVED (June 2026, fixed 10 PRC = ₹1)
// AdminUserControls removed - functionality merged into AdminUser360
// AdvancedUserManagement removed - functionality merged into AdminUser360
// AdminDeliveryPartners removed - feature deprecated
// AdminLuxuryClaims - REMOVED (deprecated feature)
// OLD AdminUser360.js deleted - replaced by AdminUser360New.js (24 March 2026)
const AdminUser360New = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminUser360New"));
const AdminFailedTransactions = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminFailedTransactions"));
// AdminRecurringDeposits - REMOVED (deprecated feature)
const AdminPerformanceReport = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminPerformanceReport"));
// AdminUnifiedPayments - REMOVED (all pending rejected + refunded)
// AdminEkoServices - REMOVED (direct recharge admin tool discontinued May 2026)
const AdminBBPSDashboard = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/Admin/AdminBBPSDashboard"));
// AdminDMTDashboard - REMOVED (Eko API not working)
const AdminLedgerView = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/Admin/AdminLedgerView"));
const AdminPopupMessages = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/Admin/AdminPopupMessages"));
const AdminBankTransfers = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/Admin/AdminBankTransfers"));
const AdminReferralBonus = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/Admin/AdminReferralBonus"));
const AdminAdAnalytics = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/Admin/AdminAdAnalytics"));
const AdminFirstPayoutQueue = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/Admin/AdminFirstPayoutQueue"));
const AdminRedeemLimits = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/Admin/AdminRedeemLimits"));
const AdminHolidays = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminHolidays"));
const AdminEmployees = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/Admin/AdminEmployees"));
const AdminEmployeeReports = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/Admin/AdminEmployeeReports"));
const AdminCommunity = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/Admin/AdminCommunity"));
const AdminCareers = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/Admin/AdminCareers"));
const AdminInvestors = IS_USER_BUILD ? null : lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/Admin/AdminInvestors"));
const CommunityPage = lazy(() => import(/* webpackChunkName: "community" */ "@/pages/CommunityPage"));
const CareersPage = lazy(() => import(/* webpackChunkName: "public" */ "@/pages/CareersPage"));
const InvestorsPage = lazy(() => import(/* webpackChunkName: "public" */ "@/pages/InvestorsPage"));
const CandidatePortal = lazy(() => import(/* webpackChunkName: "public" */ "@/pages/CandidatePortal"));
const CandidateOfferRespond = lazy(() => import(/* webpackChunkName: "public" */ "@/pages/CandidateOfferRespond"));
const EmployeeLogin = lazy(() => import(/* webpackChunkName: "public" */ "@/pages/EmployeeLogin"));
const EmployeePortal = lazy(() => import(/* webpackChunkName: "public" */ "@/pages/EmployeePortal"));
// BillPayments removed - merged into RedeemPageV2, /bill-payments redirects to /redeem
// GiftVoucherRedemption - REMOVED (deprecated April 2026)
const KYCVerification = lazy(() => import("@/pages/KYCVerification"));

// ============ MANAGER PAGES REMOVED ============
// Manager now uses Admin panel with restricted permissions
// All manager/* pages deleted - Manager uses /admin with permission-based access
// ManagerLayout removed - Manager uses AdminLayout with filtered menu items

// ============ STATIC PAGES ============
const Setup = lazy(() => import("@/pages/Setup"));
const AboutUs = lazy(() => import("@/pages/AboutUs"));
const Disclaimer = lazy(() => import("@/pages/Disclaimer"));
const ContactUs = lazy(() => import("@/pages/ContactUs"));
const ReferralCalculator = lazy(() => import("@/pages/ReferralCalculator"));
const PrcToInrConverter = lazy(() => import("@/pages/PrcToInrConverter"));
const MobileRechargeCashbackCalculator = lazy(() => import("@/pages/MobileRechargeCashbackCalculator"));
const HowToEarnMoneyOnlineIndia = lazy(() => import("@/pages/HowToEarnMoneyOnlineIndia"));
const TermsConditions = lazy(() => import("@/pages/TermsConditions"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const DeleteAccount = lazy(() => import("@/pages/DeleteAccount"));
const RefundPolicy = lazy(() => import("@/pages/RefundPolicy"));
const RewardsHome = lazy(() => import("@/pages/RewardsHome"));
const PRCStatement = lazy(() => import("@/pages/PRCStatement"));
const PRCUsageHistory = lazy(() => import("@/pages/PRCUsageHistory"));

import { API, BACKEND_URL } from "./lib/api";

// Google sign-in return: session_id arrives in the URL fragment; must be read via useLocation (reactive)
function GoogleAuthGate({ onLogin, children }) {
  const location = useLocation();
  // User app = dark theme; admin routes keep their own light look
  useEffect(() => {
    const isAdmin = location.pathname.startsWith('/admin') || location.pathname.startsWith('/manager');
    document.body.classList.toggle('pr-user-dark', !isAdmin);
  }, [location.pathname]);
  if (location.hash?.includes('session_id=')) {
    return <GoogleAuthCallback onLogin={onLogin} />;
  }
  return children;
}

function AppContent({ user, handleLogin, handleLogout, refreshUserData, setUser }) {
  const { toasts, removeToast } = useNotification();

  // Global balance update callback - updates user state + storage immediately
  const onBalanceUpdate = (newBalance) => {
    if (newBalance === undefined || newBalance === null) return;
    const updatedUser = { ...user, prc_balance: newBalance };
    setUser(updatedUser);
    setStoredUserRaw(JSON.stringify(updatedUser));
  };

  // Helper function to get role-based default route
  const getRoleBasedRoute = (user) => {
    if (!user) return "/";
    
    switch (user.role) {
      case 'admin':
      case 'sub_admin':
      case 'manager':  // Manager now goes to admin dashboard with restricted access
        return "/admin";
      case 'partner_store':
        return "/partner-store/dashboard";
      // Stockist roles deprecated - redirect to dashboard
      default:
        return "/dashboard";
    }
  };

  return (
    <MobileAppGate>
      <OfflineIndicator />
      <UpdateBanner />
      <SmartAppBanner />
      {/* HoliCelebration removed as per user request */}
      {user?.is_impersonation && <ImpersonationBanner user={user} onExit={() => { handleLogout(false); window.close(); }} />}
      <BrowserRouter>
        <PageTitleUpdater />
        <WebVitalsReporter user={user} />
        {/* Global PRC Redemption Service Charge pending banner — Feb 2026.
            Shows only when logged-in non-admin user has a PENDING 20% cash
            fee awaiting Razorpay payment. Silent otherwise. */}
        {user && !isAdminOrManager(user) && <ServiceChargePendingBanner user={user} />}
        {/* Feb 22 2026 — GlobalBannerAd (native AdMob banner) removed.
            User confirmed the banner had no measurable revenue benefit
            and Google was serving "TEST AD" placeholders on many
            production devices due to device-fingerprint auto-testing
            (see prior fork's investigation). Only rewarded interstitials
            remain, which the user explicitly asked to keep. */}
        <Suspense fallback={<LoadingFallback />}>
          <GoogleAuthGate onLogin={handleLogin}>
          <Routes>
            <Route path="/" element={
              user
                ? <Navigate to={getRoleBasedRoute(user)} replace />
                : IS_USER_BUILD
                  ? <Navigate to="/login" replace />
                  : <RewardsHome />
            } />
            {/* home-old route removed - using RewardsHome */}
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogArticle />} />
            <Route path="/login" element={user ? <Navigate to={getRoleBasedRoute(user)} /> : <Login onLogin={handleLogin} />} />
            <Route path="/change-device" element={<Suspense fallback={<LoadingFallback />}><ChangeDevice /></Suspense>} />
            <Route path="/careers" element={<Suspense fallback={<LoadingFallback />}><CareersPage /></Suspense>} />
            <Route path="/candidate/:appId" element={<Suspense fallback={<LoadingFallback />}><CandidatePortal /></Suspense>} />
            <Route path="/candidate/offer/:token" element={<Suspense fallback={<LoadingFallback />}><CandidateOfferRespond /></Suspense>} />
            <Route path="/employee/login" element={<Suspense fallback={<LoadingFallback />}><EmployeeLogin /></Suspense>} />
            <Route path="/employee/portal" element={<Suspense fallback={<LoadingFallback />}><EmployeePortal /></Suspense>} />
            <Route path="/investors" element={<Suspense fallback={<LoadingFallback />}><InvestorsPage /></Suspense>} />
            <Route path="/register" element={user ? <Navigate to={getRoleBasedRoute(user)} /> : <RegisterSimple />} />
            <Route path="/set-new-pin" element={<SetNewPin onLogin={handleLogin} />} />
            <Route path="/forgot-password" element={user ? <Navigate to={getRoleBasedRoute(user)} /> : <ForgotPassword />} />
            <Route path="/reset-password" element={<Suspense fallback={<LoadingFallback />}><ResetPassword /></Suspense>} />
            <Route path="/forgot-pin" element={user ? <Navigate to={getRoleBasedRoute(user)} /> : <ForgotPin />} />
            <Route path="/setup" element={<Setup />} />
            
            {/* Static Pages */}
            <Route path="/about" element={<AboutUs />} />
            <Route path="/about-us" element={<Navigate to="/about" replace />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/terms" element={<TermsConditions />} />
            <Route path="/terms-conditions" element={<Navigate to="/terms" replace />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/contact-us" element={<Navigate to="/contact" replace />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/delete-account" element={<Suspense fallback={<LoadingFallback />}><DeleteAccount /></Suspense>} />
            <Route path="/delete.html" element={<Navigate to="/delete-account" replace />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/refund" element={<RefundPolicy />} />
            <Route path="/rewards-home" element={<RewardsHome />} />

            {/* SEO Landing Pages (public, no auth) */}
            <Route path="/referral-calculator" element={<Suspense fallback={<LoadingFallback />}><ReferralCalculator /></Suspense>} />
            <Route path="/calculator" element={<Navigate to="/referral-calculator" replace />} />
            <Route path="/earnings-calculator" element={<Navigate to="/referral-calculator" replace />} />
            <Route path="/prc-to-inr-converter" element={<Suspense fallback={<LoadingFallback />}><PrcToInrConverter /></Suspense>} />
            <Route path="/prc-to-inr" element={<Navigate to="/prc-to-inr-converter" replace />} />
            <Route path="/recharge-cashback-calculator" element={<Suspense fallback={<LoadingFallback />}><MobileRechargeCashbackCalculator /></Suspense>} />
            <Route path="/cashback-calculator" element={<Navigate to="/recharge-cashback-calculator" replace />} />
            <Route path="/how-to-earn-money-online-india" element={<Suspense fallback={<LoadingFallback />}><HowToEarnMoneyOnlineIndia /></Suspense>} />
            <Route path="/earn-money-online" element={<Navigate to="/how-to-earn-money-online-india" replace />} />
            
            {/* Protected Routes - User Only (Admin/Manager redirected to /admin) */}
            <Route path="/dashboard" element={user ? (isAdminOrManager(user) ? <Navigate to="/admin" /> : <DashboardModern user={user} onLogout={handleLogout} />) : <Navigate to="/login" />} />
            <Route path="/community" element={user ? <Suspense fallback={<LoadingFallback />}><CommunityPage user={user} /></Suspense> : <Navigate to="/login" />} />
            {/* fintech route removed - not in use */}
            <Route path="/support" element={user ? (isAdminOrManager(user) ? <Navigate to="/admin" /> : <SupportTickets user={user} onLogout={handleLogout} />) : <Navigate to="/login" />} />
            <Route path="/daily-rewards" element={<Navigate to="/dashboard" />} /> {/* Rewards integrated into Dashboard */}
            <Route path="/mining" element={<Navigate to="/dashboard" />} /> {/* Redirect old route */}
            {/* TapGame removed - feature deprecated */}
            <Route path="/game" element={<Navigate to="/dashboard" replace />} />
            {/* Removed: Treasure Hunt and Scratch Card games */}
            <Route path="/referrals" element={user ? (isAdminOrManager(user) ? <Navigate to="/admin" /> : <Referrals user={user} onLogout={handleLogout} refreshUserData={refreshUserData} />) : <Navigate to="/login" />} />
            <Route path="/my-referral-bonus" element={user ? (isAdminOrManager(user) ? <Navigate to="/admin" /> : <Suspense fallback={<LoadingFallback />}><MyReferralBonus user={user} /></Suspense>) : <Navigate to="/login" />} />
            <Route path="/my-service-charges" element={user ? (isAdminOrManager(user) ? <Navigate to="/admin" /> : <Suspense fallback={<LoadingFallback />}><MyServiceCharges user={user} /></Suspense>) : <Navigate to="/login" />} />
            <Route path="/my-redeems/service-charges" element={<Navigate to="/my-service-charges" replace />} />
            <Route path="/referrals/live-feed" element={user ? <DownlineLiveFeed user={user} /> : <Navigate to="/login" />} />
            <Route path="/referrals/dashboard" element={<Navigate to="/referrals" replace />} />
            <Route path="/referrals/ai" element={<Navigate to="/referrals" replace />} />
            <Route path="/network" element={<Navigate to="/referrals" replace />} />
            {/* Marketplace removed - feature deprecated */}
            <Route path="/marketplace" element={<Navigate to="/dashboard" replace />} />
            <Route path="/flash-sales" element={<Navigate to="/dashboard" replace />} />
            {/* Orders removed - Marketplace deprecated (December 2025) */}
            <Route path="/orders" element={<Navigate to="/dashboard" replace />} />
            {/* PRC Redeem Statement */}
            <Route path="/prc-statement" element={user ? <Suspense fallback={<LoadingFallback />}><PRCStatement user={user} /></Suspense> : <Navigate to="/login" />} />
            <Route path="/usage-history" element={user ? <Suspense fallback={<LoadingFallback />}><PRCUsageHistory user={user} /></Suspense> : <Navigate to="/login" />} />
            {/* Removed: Activity page */}
            <Route path="/vip" element={<Navigate to="/dashboard" replace />} />
            <Route path="/subscription" element={user ? (isAdminOrManager(user) ? <Navigate to="/admin" /> : <Suspense fallback={<LoadingFallback />}><SubscriptionPlans user={user} onLogout={handleLogout} onBalanceUpdate={onBalanceUpdate} /></Suspense>) : <Navigate to="/login" />} />
            <Route path="/my-invoices" element={user ? (isAdminOrManager(user) ? <Navigate to="/admin" /> : <MyInvoices user={user} />) : <Navigate to="/login" />} />
            <Route path="/my-reports" element={user ? <Suspense fallback={<LoadingFallback />}><MyReports user={user} /></Suspense> : <Navigate to="/login" />} />
            <Route path="/employee/my-reports" element={<Navigate to="/my-reports" replace />} />
            <Route path="/invoices" element={<Navigate to="/my-invoices" replace />} />
            <Route path="/kyc" element={user ? (isAdminOrManager(user) ? <Navigate to="/admin" /> : <KYCVerification user={user} />) : <Navigate to="/login" />} />
            {/* Removed: Wallet/Withdrawal functionality */}
            {/* Removed: Leaderboard page for AdMob compliance */}
            {/* Removed: ReferralEarningsHistory page */}
            {/* Removed: Gamification/Achievements page for AdMob compliance */}
            <Route path="/profile" element={user ? (isAdminOrManager(user) ? <Navigate to="/admin" /> : <ProfileAdvanced user={user} onLogout={handleLogout} />) : <Navigate to="/login" />} />
            {/* Legacy route redirect - keeping for backward compatibility */}
            <Route path="/profile-advanced" element={<Navigate to="/profile" replace />} />
            
            {/* Social Feature Routes - User Only */}
            <Route path="/profile/:uid" element={<PublicProfile user={user} />} />
            <Route path="/network-feed" element={user ? (isAdminOrManager(user) ? <Navigate to="/admin" /> : <NetworkFeed user={user} />) : <Navigate to="/login" />} />
            <Route path="/messages" element={user ? (isAdminOrManager(user) ? <Navigate to="/admin" /> : <Messages user={user} />) : <Navigate to="/login" />} />
            <Route path="/messages/:recipientUid" element={user ? (isAdminOrManager(user) ? <Navigate to="/admin" /> : <Messages user={user} />) : <Navigate to="/login" />} />
            <Route path="/followers/:uid" element={<FollowersList user={user} type="followers" />} />
            <Route path="/following/:uid" element={<FollowersList user={user} type="following" />} />
            <Route path="/notifications" element={user ? (isAdminOrManager(user) ? <Navigate to="/admin" /> : <Notifications user={user} />) : <Navigate to="/login" />} />
            {/* /luxury-life route REMOVED - deprecated feature */}
            {/* /recurring-deposit route REMOVED - deprecated feature */}
            <Route path="/recurring-deposit" element={<Navigate to="/dashboard" replace />} />
            <Route path="/rd" element={<Navigate to="/dashboard" replace />} />
            {/* /network-tree route REMOVED - orphan page, replaced by L1-L5 breakdown on /referrals */}
            <Route path="/network-tree" element={<Navigate to="/referrals" replace />} />

            {/* Paras Mall - reward shopping */}
            <Route
              path="/mall"
              element={user ? (
                isAdminOrManager(user) ? <Navigate to="/admin/mall" replace /> :
                <RouteErrorBoundary routeName="mall">
                <Suspense fallback={<LoadingFallback />}>
                  <ParasMall user={user} onBalanceUpdate={onBalanceUpdate} />
                </Suspense>
                </RouteErrorBoundary>
              ) : <Navigate to="/login" />}
            />
            <Route
              path="/mall/wishlist"
              element={user ? (
                <Suspense fallback={<LoadingFallback />}>
                  <MallWishlist />
                </Suspense>
              ) : <Navigate to="/login" />}
            />
            <Route path="/bank-redeem" element={user ? (isAdminOrManager(user) ? <Navigate to="/admin" /> : <Suspense fallback={<LoadingFallback />}><BankRedeemPage user={user} onLogout={handleLogout} onBalanceUpdate={onBalanceUpdate} /></Suspense>) : <Navigate to="/login" />} />
            <Route path="/prc-to-bank" element={<Navigate to="/dashboard" replace />} />
            {/* Redeem PRC routes - DEPRECATED April 2026, redirect to dashboard */}
            <Route path="/dmt" element={<Navigate to="/dashboard" replace />} />
            <Route path="/fund-transfer" element={<Navigate to="/dashboard" replace />} />
            <Route path="/bank-transfer" element={<Navigate to="/dashboard" replace />} />
            <Route path="/money-transfer" element={<Navigate to="/dashboard" replace />} />
            <Route path="/bbps" element={<Navigate to="/dashboard" replace />} />
            <Route path="/bill-pay" element={<Navigate to="/dashboard" replace />} />
            
            {/* Stock requests removed - stockist system deprecated */}
            
            {/* ========== ADMIN ROUTES - Excluded from User Build (Play Store AAB) ========== */}
            {!IS_USER_BUILD && AdminLayout && (
              <>
                <Route
                  path="/admin/mall"
                  element={canAccessAdmin(user) ? (
                    <Suspense fallback={<LoadingFallback />}>
                      <AdminLayout user={user} onLogout={handleLogout}>
                        <AdminParasMall />
                      </AdminLayout>
                    </Suspense>
                  ) : <Navigate to="/dashboard" />}
                />
                <Route
                  path="/admin/mall/analytics"
                  element={canAccessAdmin(user) ? (
                    <Suspense fallback={<LoadingFallback />}>
                      <AdminLayout user={user} onLogout={handleLogout}>
                        <AdminMallAnalytics />
                      </AdminLayout>
                    </Suspense>
                  ) : <Navigate to="/dashboard" />}
                />
                <Route path="/admin" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminDashboard user={user} onLogout={handleLogout} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/observability" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminObservability user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/dashboard" element={canAccessAdmin(user) ? <Navigate to="/admin" replace /> : <Navigate to="/dashboard" />} />
                <Route path="/admin/users" element={<Navigate to="/admin/user-360" replace />} />
                <Route path="/admin/analytics" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminAnalytics user={user} onLogout={handleLogout} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/prc-analytics" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminPRCAnalytics user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/company-wallets" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminCompanyWallets user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/ads-income" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminAdsIncome user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/fraud-alerts" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminFraudAlerts user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/fraud-dashboard" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminFraudDashboard user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/audit" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminAuditService user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/fixed-expenses" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminFixedExpenses user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/profit-loss" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminProfitLoss user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/gst-report" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminGSTReport user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/invoices" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminInvoicesList user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/bulk-refund-otp" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminBulkRefundOTP user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/liquidity" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminLiquidity user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/video-ads" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminVideoAds user={user} onLogout={handleLogout} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/settings-hub" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminSettingsHub user={user} onLogout={handleLogout} /></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/partners" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminPartners user={user} onLogout={handleLogout} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/partner-stores" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminPartnerStores user={user} onLogout={handleLogout} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                {/* /partner-store/dashboard and /pay-partner-store moved OUT of the IS_USER_BUILD block — see below. Feb 17 2026. */}
                <Route path="/admin/device-binding" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminDeviceBinding user={user} onLogout={handleLogout} /></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/prc-rate-control" element={<Navigate to="/admin" replace />} />
                <Route path="/admin/settings" element={canAccessAdmin(user) ? <Navigate to="/admin/settings-hub?tab=payment" replace /> : <Navigate to="/dashboard" />} />
                <Route path="/admin/security" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminSecurityDashboard user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/error-monitor" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminErrorMonitor user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/health-check" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminHealthCheck user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/force-activate-subscription" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminForceActivateSubscription user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/web-vitals" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminWebVitals user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/holidays" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminHolidays /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/settings/system" element={canAccessAdmin(user) ? <Navigate to="/admin/settings-hub?tab=system" replace /> : <Navigate to="/dashboard" />} />
                <Route path="/admin/settings/web" element={canAccessAdmin(user) ? <Navigate to="/admin/settings-hub?tab=web" replace /> : <Navigate to="/dashboard" />} />
                <Route path="/admin/settings/social" element={canAccessAdmin(user) ? <Navigate to="/admin/settings-hub?tab=social" replace /> : <Navigate to="/dashboard" />} />
                <Route path="/admin/settings/redeem" element={canAccessAdmin(user) ? <Navigate to="/admin/settings-hub?tab=redeem" replace /> : <Navigate to="/dashboard" />} />
                {/* AdminRedeemSettings - REMOVED (deprecated March 2026) */}
                <Route path="/admin/redeem-settings" element={<Navigate to="/admin" replace />} />
                <Route path="/admin/pending-requests" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminPendingRequests user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/user-ledger" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminUserLedger user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/capital" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminCapitalManagement user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/accounting" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminAccountingDashboard user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/cash-bank-book" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminCashBankBook user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/prc-ledger" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminPRCLedger user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/transaction-manager" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminTransactionManager user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/financial-reports" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminFinancialReports user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/capital-management" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminCapitalManagement user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/trial-balance" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminTrialBalance user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/accounts-receivable" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminAccountsReceivable user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/accounts-payable" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminAccountsPayable user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/financial-ratios" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminFinancialRatios user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/prc-economy" element={<Navigate to="/admin" replace />} />
                <Route path="/admin/user-controls" element={<Navigate to="/admin/user360" />} />
                <Route path="/admin/user-360" element={<Navigate to="/admin/user360" />} />
                <Route path="/admin/user360" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminUser360New user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/failed-transactions" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminFailedTransactions user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/vip-plans" element={<Navigate to="/admin/subscriptions" replace />} />
                {/* AdminPRCBurnControl - REMOVED, redirect to admin */}
                <Route path="/admin/prc-burn-control" element={<Navigate to="/admin" replace />} />
                <Route path="/admin/data-backup" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminDataBackup user={user} onLogout={handleLogout} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                {/* AdminBillPayments - REMOVED, redirect to admin */}
                <Route path="/admin/bill-payments" element={<Navigate to="/admin" replace />} />
                <Route path="/admin/razorpay-subscriptions" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminRazorpaySubscriptions user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                {/* /admin/bank-withdrawals merged into /admin/unified-payments */}
                <Route path="/admin/bank-withdrawals" element={<Navigate to="/admin/unified-payments" replace />} />
                <Route path="/admin/gift-vouchers" element={<Navigate to="/admin" replace />} />
                {/* /admin/luxury-claims route REMOVED - deprecated feature */}
                {/* /admin/recurring-deposits route REMOVED - deprecated feature */}
                <Route path="/admin/recurring-deposits" element={<Navigate to="/admin" replace />} />
                <Route path="/admin/rd" element={<Navigate to="/admin" replace />} />
                {/* AdminUnifiedPayments - REMOVED, redirect to admin */}
                <Route path="/admin/unified-payments" element={<Navigate to="/admin" replace />} />
                <Route path="/admin/eko-services" element={<Navigate to="/admin" replace />} />
                {/* AdminRedeemDashboard - REMOVED (deprecated March 2026, no pending requests) */}
                <Route path="/admin/redeem" element={<Navigate to="/admin/bbps" replace />} />
                <Route path="/admin/bbps-requests" element={<Navigate to="/admin/bbps" replace />} />
                <Route path="/admin/bbps" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminBBPSDashboard user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                {/* Admin DMT routes REMOVED - Eko API not working, redirect to admin */}
                <Route path="/admin/dmt" element={<Navigate to="/admin" replace />} />
                <Route path="/admin/dmt-transactions" element={<Navigate to="/admin" replace />} />
                <Route path="/admin/dmt-refunds" element={<Navigate to="/admin" replace />} />
                <Route path="/admin/ledger" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminLedgerView user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/popup-messages" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminPopupMessages user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/bank-transfers" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminBankTransfers user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/referral-bonus" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminReferralBonus user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/ad-analytics" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminAdAnalytics user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/bank-transfers/first-payout-queue" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminFirstPayoutQueue user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/redeem-limits" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminRedeemLimits user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/performance-report" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminPerformanceReport user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/service-charges" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminServiceCharges user={user} onLogout={handleLogout} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/finance-metrics" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminFinanceMetrics /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/bill-service-charges" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminBillServiceCharges user={user} onLogout={handleLogout} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/service-toggles" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminServiceToggles user={user} onLogout={handleLogout} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/policies" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminPolicies user={user} onLogout={handleLogout} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/kyc" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminKYC user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/inactive-cleanup" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminInactiveCleanup user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/prc-lock" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminPRCLock user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/economy-settings" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminEconomySettings /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/payments" element={<Navigate to="/admin/subscriptions" replace />} />
                {/* Admin Orders removed - Marketplace deprecated (December 2025) */}
                <Route path="/admin/orders" element={<Navigate to="/admin" replace />} />
                <Route path="/admin/marketplace" element={<Navigate to="/admin" replace />} />
                <Route path="/admin/delivery-partners" element={<Navigate to="/admin" replace />} />
                <Route path="/admin/support" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminSupport user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/chatbot-withdrawals" element={<Navigate to="/admin" replace />} />
                <Route path="/admin/contact-submissions" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminContactSubmissions user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/contact-settings" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminContactSettings user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/vip-verification" element={<Navigate to="/admin/subscriptions" replace />} />
                <Route path="/admin/subscriptions" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminSubscriptionManagement user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                {/* /admin/core-team removed Feb 17 2026 — Core Team feature retired. Redirect any deep links to /admin/dashboard. */}
                <Route path="/admin/core-team" element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="/admin/employees" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminEmployees user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/employees/reports" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminEmployeeReports user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/community" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminCommunity user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/careers" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminCareers user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
                <Route path="/admin/investors" element={canAccessAdmin(user) ? <Suspense fallback={<LoadingFallback />}><AdminLayout user={user} onLogout={handleLogout}><AdminInvestors user={user} /></AdminLayout></Suspense> : <Navigate to="/dashboard" />} />
              </>
            )}
            
            {/* In USER build (Android AAB): catch all /admin/* and open in external browser */}
            {IS_USER_BUILD && (
              <Route path="/admin/*" element={<AdminOnWebOnly />} />
            )}
            
            {/* Partner Store user-facing routes — MUST be outside IS_USER_BUILD
                block. Fixes APK blank-page bug (Feb 17 2026): before this move,
                /pay-partner-store was declared inside the admin block, so the
                Android AAB build stripped it out entirely, showing users a
                blank shell when they tapped "Pay to Partner Store". */}
            <Route
              path="/pay-partner-store"
              element={user && user.role !== 'partner_store'
                ? <RouteErrorBoundary routeName="pay-partner-store"><Suspense fallback={<LoadingFallback />}><PayPartnerStore user={user} /></Suspense></RouteErrorBoundary>
                : <Navigate to="/login" replace />}
            />
            <Route
              path="/partner-store/dashboard"
              element={user?.role === 'partner_store'
                ? <Suspense fallback={<LoadingFallback />}><PartnerStoreDashboard user={user} onLogout={handleLogout} /></Suspense>
                : <Navigate to="/login" replace />}
            />

            {/* Redeem PRC routes - DEPRECATED April 2026 */}
            <Route path="/redeem" element={<Navigate to="/dashboard" replace />} />
            <Route path="/withdrawal-history" element={<Navigate to="/dashboard" replace />} />
            <Route path="/bill-history" element={<Navigate to="/dashboard" replace />} />
            <Route path="/bill-payments" element={<Navigate to="/dashboard" replace />} />
            <Route path="/gift-vouchers" element={<Navigate to="/dashboard" replace />} />
            {/* Manager routes now redirect to Admin - Manager uses Admin panel with permission-based access */}
            <Route path="/manager" element={<Navigate to="/admin" replace />} />
            <Route path="/manager/*" element={<Navigate to="/admin" replace />} />
            {/* All stockist routes removed - using direct delivery partner model */}
          </Routes>
          </GoogleAuthGate>
        </Suspense>
        {/* Professional Navigation System - Only for regular users, not admin/manager roles */}
        {user && !['admin', 'sub_admin', 'manager'].includes(user.role) && (
          <>
            <TopBar user={user} onLogout={handleLogout} />
            <BottomNav />
            <LiveTickerStrip />
          </>
        )}
        {/* Admin Popup Message - Shows for logged-in users only */}
        <PopupMessage />
      </BrowserRouter>
      <Toaster
        position="top-center"
        richColors
        visibleToasts={2}
        toastOptions={{
          duration: 3000,
          // Sep 1 2026: removed `marginTop: '40vh'` — it was pushing every
          // toast to the middle of the viewport and blocking real content
          // (mining stats, session progress, admin dashboard cards). The
          // safe-area-inset padding below already clears the status bar /
          // notch on notched Android devices without covering primary UI.
        }}
        offset="calc(env(safe-area-inset-top, 0px) + 12px)"
      />
      {/* Duplicate ToastContainer removed - Sonner handles all toasts */}
    </MobileAppGate>
  );
}

function App() {
  // Initialize AdMob + App Open Ad at app boot (no-op on web; lifecycle observer
  // attached natively shows the App Open ad on every foreground after cold start).
  useAdMob();

  // Capture Play Store install referrer once per app launch (native-only).
  // If MobileAppGate forwarded a ref code to Play Store via &referrer=ref%3DXYZ,
  // this writes it to localStorage so the in-app register screen picks it up.
  useEffect(() => { captureInstallReferrer(); }, []);

  // Initialize user from storage (sessionStorage if impersonation tab, else localStorage)
  const [user, setUser] = useState(() => {
    const storedUser = getStoredUserRaw();
    return storedUser ? JSON.parse(storedUser) : null;
  });
  const [loading, setLoading] = useState(true);
  const [roleValidated, setRoleValidated] = useState(false);

  // SECURITY: Validate user role via API instead of trusting localStorage
  const validateUserRole = async (storedUser) => {
    if (!storedUser?.token) return null;

    // Impersonation sessions use opaque IMP_ tokens (not JWTs).
    // They are validated via /api/auth/validate-session instead.
    // Skip /auth/me here to avoid a 401 that would clear the session.
    if (storedUser.is_impersonation || (typeof storedUser.token === 'string' && storedUser.token.startsWith('IMP_'))) {
      return storedUser;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${storedUser.token}`
        }
      });
      
      if (response.ok) {
        const verifiedUser = await response.json();
        // Update role from server-verified response
        const updatedUser = {
          ...storedUser,
          role: verifiedUser.role,
          is_admin: verifiedUser.is_admin,
          verified: verifiedUser.verified
        };
        
        // If role mismatch, update storage
        if (storedUser.role !== verifiedUser.role) {
          console.warn('[SECURITY] Role mismatch detected:', {
            stored: storedUser.role,
            verified: verifiedUser.role
          });
          setStoredUserRaw(JSON.stringify(updatedUser));
        }
        
        return updatedUser;
      } else if (response.status === 401) {
        // Token expired or invalid - logout
        console.warn('[SECURITY] Token invalid, clearing session');
        removeStoredUser();
        localStorage.removeItem("paras_session_token");
        localStorage.removeItem("token");
        return null;
      }
    } catch (error) {
      console.error('[SECURITY] Role validation failed:', error);
    }
    return storedUser; // Fallback to stored user on network error
  };

  // Refresh user data from server to ensure subscription info is current
  const refreshUserData = async (uid) => {
    // Add timeout to prevent infinite loading
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout

    try {
      // SECURITY (Phase B Apr 30 2026): /api/user/{uid} now requires JWT auth.
      // Pull token from storage and send Authorization header explicitly,
      // since this raw fetch() bypasses the axios interceptor.
      const stored = JSON.parse(getStoredUserRaw() || "{}");
      const authToken =
        stored.token ||
        localStorage.getItem("token") ||
        localStorage.getItem("paras_session_token");
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};

      const response = await fetch(`${BACKEND_URL}/api/user/${uid}`, {
        signal: controller.signal,
        headers,
      });
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const freshData = await response.json();
        // CRITICAL: Preserve admin/sub_admin/manager role from stored user
        // API might return 'user' as default, but we must keep elevated roles
        const storedUser = JSON.parse(getStoredUserRaw() || "{}");
        const storedRole = storedUser.role;
        const freshRole = freshData.role;
        
        // DEBUG: Log role comparison
        /* console.log('[REFRESH] Role comparison:', {
          storedRole,
          freshRole,
          storedUserKeys: Object.keys(storedUser).length
        }); */
        
        // Priority: Keep admin roles from login, don't let refresh override them
        let finalRole = 'user';
        if (['admin', 'sub_admin', 'manager'].includes(storedRole)) {
          // NEVER override admin roles - they come from login API with proper auth
          finalRole = storedRole;
          // console.log('[REFRESH] Keeping admin role from stored:', finalRole);
        } else if (freshRole && freshRole !== 'user') {
          // Use fresh role if it's elevated (API returned a real role)
          finalRole = freshRole;
          // console.log('[REFRESH] Using elevated fresh role:', finalRole);
        } else {
          // Default case - use stored or fresh, prefer stored
          finalRole = storedRole || freshRole || 'user';
          // console.log('[REFRESH] Using default role logic:', finalRole);
        }
        
        const updatedUser = {
          ...storedUser,
          ...freshData,
          role: finalRole
        };
        setUser(updatedUser);
        // Strip sensitive fields before persisting to storage
        const { hashed_pin: _hp, pin_hash: _ph, password: _pw, ...safeData } = updatedUser;
        setStoredUserRaw(JSON.stringify(safeData));
        // console.log('[REFRESH] Final user role set to:', finalRole);
        return updatedUser;
      }
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        console.warn('[REFRESH] Request timed out after 15 seconds');
      } else {
        console.error('Error refreshing user data:', error);
      }
    }
    return null;
  };

  useEffect(() => {
    // Native: apply branded status bar colour on app boot (no-op on web)
    applyBrandedStatusBar();

    // Native: sync the app launcher icon badge with backend unread count
    let badgeInterval = null;
    if (user?.uid) {
      syncAppBadgeFromBackend(user.uid);
      badgeInterval = setInterval(() => syncAppBadgeFromBackend(user.uid), 60_000);
    }

    // If user exists, validate role via API and refresh data from server.
    // SECURITY: Validate role server-side before allowing admin access.
    //
    // ── BUG FIX (Jun 24, 2026) ─────────────────────────────────────
    // Previously this block contained an `if (user?.uid) { ... return () => clearInterval(t); }`
    // EARLY RETURN before the initializeUser() call below. That meant
    // every logged-in user who refreshed the dashboard hit the early
    // return, `initializeUser()` was never called, and `setLoading(false)`
    // never fired — so the App stayed on the light-purple "Loading…"
    // screen forever. We now do badge-interval + initializeUser in the
    // same effect, and the cleanup function tears down BOTH.
    let timeoutFallback;

    const initializeUser = async () => {
      if (user?.uid) {
        // First validate role via /auth/me API
        const validatedUser = await validateUserRole(user);
        
        if (!validatedUser) {
          // Token invalid - clear user
          setUser(null);
          setLoading(false);
          setRoleValidated(true);
          return;
        }
        
        // Update user with validated role
        if (validatedUser.role !== user.role) {
          setUser(validatedUser);
        }
        setRoleValidated(true);
        
        // Then refresh full user data
        await refreshUserData(user.uid);
        setLoading(false);
      } else {
        setLoading(false);
        setRoleValidated(true);
      }
    };
    
    timeoutFallback = setTimeout(() => {
      console.warn('[APP] Loading fallback triggered after 20 seconds');
      setLoading(false);
      setRoleValidated(true);
    }, 20000);
    
    initializeUser().finally(() => {
      clearTimeout(timeoutFallback);
    });

    return () => {
      if (badgeInterval) clearInterval(badgeInterval);
      if (timeoutFallback) clearTimeout(timeoutFallback);
    };
  }, []); // Run once on mount

  const handleLogin = async (userData) => {
    // DEBUG: Log login data for troubleshooting admin redirect
    /* console.log('[LOGIN] User data received:', {
      uid: userData.uid,
      role: userData.role,
      name: userData.name
    }); */
    
    // Save session token for single-session enforcement
    if (userData.session_token) {
      localStorage.setItem("paras_session_token", userData.session_token);
    }
    // Save token directly for reliable access across components
    if (userData.token) {
      localStorage.setItem("token", userData.token);
    }
    setUser(userData);
    // Strip sensitive fields before persisting to storage
    const { hashed_pin, pin_hash, password, ...safeUserData } = userData;
    setStoredUserRaw(JSON.stringify(safeUserData));

    // NOTE (Apr 30 2026): Removed the post-login refreshUserData() call.
    // The /api/auth/login response already contains complete user payload
    // (subscription, balance, role, mining state). The mount-time effect
    // below also refreshes once on first render, so a third hit was wasteful
    // and added ~500ms to the login → dashboard transition.
  };

  const handleLogout = async (showMessage = true, reason = null) => {
    const storedUser = JSON.parse(getStoredUserRaw() || "{}");
    // Native: cancel any scheduled session-expiry warning notification
    cancelSessionExpiryWarning();
    // Native: clear the launcher icon badge on logout
    clearAppBadge();
    
    // Clear session on server
    if (storedUser?.uid) {
      try {
        await fetch(`${BACKEND_URL}/api/auth/logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ uid: storedUser.uid })
        });
      } catch (e) {
        console.error('Logout API error:', e);
      }
    }
    
    setUser(null);
    removeStoredUser();
    localStorage.removeItem("paras_session_token");
    localStorage.removeItem("token");
    
    if (showMessage) {
      if (reason === 'session_expired') {
        toast.error("You have logged in on another device. Please login again.", { duration: 5000 });
      } else {
        toast.success("Logged out successfully");
      }
    }
    
    // Redirect to home page after logout
    setTimeout(() => {
      window.location.href = '/';
    }, 500);
  };

  // Session validation - every 90s, paused while tab is hidden (Apr 30 2026 perf)
  // Old behaviour: every 30s regardless of tab state. New behaviour:
  //   - First check 5s after mount/login (race-condition safe).
  //   - Then every 90s (was 30s) — auth tokens last hours, single-session
  //     enforcement doesn't need second-by-second polling.
  //   - When tab becomes hidden (visibilityState !== 'visible'), the timer
  //     stops to save battery + API traffic.
  //   - When tab becomes visible again, run an immediate check then resume
  //     the 90s loop.
  useEffect(() => {
    if (!user?.uid) return;

    const validateSession = async () => {
      const sessionToken = localStorage.getItem("paras_session_token");
      if (!sessionToken) return;
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/validate-session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ uid: user.uid, session_token: sessionToken })
        });
        const result = await response.json();
        if (!result.valid && result.reason === 'session_expired') {
          // Another device logged in - force logout
          handleLogout(true, 'session_expired');
        }
      } catch (e) {
        console.error('Session validation error:', e);
      }
    };

    let interval = null;
    const startInterval = () => {
      if (interval) return;
      interval = setInterval(validateSession, 90000);
    };
    const stopInterval = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Tab back in focus — run immediate check then resume polling.
        validateSession();
        startInterval();
      } else {
        // Tab hidden — stop polling to save battery/API.
        stopInterval();
      }
    };

    // First validation 5s after mount (race-safe with login flow).
    const initialDelay = setTimeout(validateSession, 5000);
    if (document.visibilityState === 'visible') startInterval();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearTimeout(initialDelay);
      stopInterval();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [user?.uid]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <LanguageProvider>
      <NotificationProvider userId={user?.uid}>
        <AppContent user={user} handleLogin={handleLogin} handleLogout={handleLogout} refreshUserData={refreshUserData} setUser={setUser} />
      </NotificationProvider>
    </LanguageProvider>
  );
}

export default App;
